import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, money, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { AdminProductRow, AdminNewProduct } from "@/components/AdminProducts";
import { AdminDashboard } from "@/components/AdminDashboard";
import { AdminClips } from "@/components/AdminClips";
import { isAiPhoto } from "@/components/AdminProductDetails";
import { LowMarginPanel, lowMargin } from "@/components/AdminMargins";
import { AdminDrafts } from "@/components/AdminDrafts";
import { AdminChats } from "@/components/AdminChats";
import { AdminReviews } from "@/components/AdminReviews";
import { AdminMessages } from "@/components/AdminMessages";
import { AdminBundles } from "@/components/AdminBundles";
import { AdminWeeklyReport } from "@/components/AdminWeeklyReport";
import { AdminMerchantSetup } from "@/components/AdminMerchantSetup";
import { AdminLiveCheck } from "@/components/AdminLiveCheck";
import { AdminBlog } from "@/components/AdminBlog";
import { AdminSharedPhotos, sharedSlugs } from "@/components/AdminSharedPhotos";
import { AdminStripeSetup } from "@/components/AdminStripeSetup";
import { ADMIN, ACCOUNT } from "@/constants/testIds";

const OrderRow = ({ order, onSaved }) => {
  const [tracking, setTracking] = useState(order.tracking || "");
  const [labelCost, setLabelCost] = useState(order.label_cost ?? "");
  const shipped = order.status === "shipped";
  const toggle = async () => {
    try {
      const { data } = await api.put(`/admin/orders/${order.order_id}/ship`, { shipped: !shipped, tracking: tracking || null, label_cost: labelCost === "" ? null : Number(labelCost) });
      onSaved(data);
      toast.success(shipped ? "Marked as not shipped" : order.email ? "Marked as shipped. Customer emailed" : "Marked as shipped");
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  const resend = async () => {
    try {
      const { data } = await api.post(`/admin/orders/${order.order_id}/resend-shipped`);
      onSaved(data);
      toast.success("Resending the shipping email");
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  return (
    <li data-testid={ADMIN.orderRow(order.order_id)} className="py-4 grid md:grid-cols-[1fr_1.2fr_0.9fr_auto] gap-3 md:items-center">
      <div>
        <p className="font-heading font-bold">{order.order_id} · {money(order.amount_paid ?? order.total)}
          {(order.test_order || order.live_check) && (
            <span data-testid={`admin-order-test-tag-${order.order_id}`} title="Not counted in sales numbers" className="ml-2 align-middle px-2 py-0.5 rounded-full bg-[#F0EBE1] text-stone-600 text-[11px] font-semibold uppercase tracking-wide">
              {order.live_check ? "$1 live check" : "Test order"}
            </span>
          )}
        </p>
        <p className="text-sm text-stone-500">{new Date(order.created_at).toLocaleString()} · {order.email || "no email"}</p>
        <p className="text-sm text-stone-600 mt-1">{order.lines.map((l) => `${l.qty}× ${l.name}`).join(", ")}</p>
      </div>
      <input data-testid={ADMIN.trackingInput(order.order_id)} value={tracking} onChange={(e) => setTracking(e.target.value)} placeholder="Tracking number (optional)" className="px-3 py-2 rounded-xl border border-[#E7E0D3] text-sm" />
      <input data-testid={ADMIN.labelCost(order.order_id)} type="number" step="0.01" min="0" value={labelCost} onChange={(e) => setLabelCost(e.target.value)} placeholder="Shipping label cost $ (optional)" aria-label="Shipping label cost" className="px-3 py-2 rounded-xl border border-[#E7E0D3] text-sm" />
      <button data-testid={ADMIN.shipButton(order.order_id)} onClick={toggle} className={`px-4 py-2 rounded-full text-sm transition-colors ${shipped ? "bg-[#E8F0EC] text-[#2C5242]" : "bg-[#1C1917] text-white hover:bg-[#E06D44]"}`}>
        {shipped ? "Shipped ✓ (undo)" : "Mark as shipped"}
      </button>
      {shipped && order.email && (
        <p data-testid={`admin-ship-email-${order.order_id}`} className="md:col-span-4 text-xs text-stone-500">
          {order.shipped_email_sent === true && "Shipping email sent to the customer."}
          {order.shipped_email_sent === false && (
            <>Shipping email didn't send. <button onClick={resend} data-testid={`admin-resend-${order.order_id}`} className="underline text-[#E06D44]">Resend</button></>
          )}
          {order.shipped_email_sent == null && "Sending the shipping email…"}
        </p>
      )}
    </li>
  );
};

export default function AdminPage() {
  const { user, loading, login } = useAuth();
  const [allowed, setAllowed] = useState(null);
  const [tab, setTab] = useState("orders");
  const [products, setProducts] = useState([]);
  const [orders, setOrders] = useState([]);
  const [q, setQ] = useState("");
  const [needsPhoto, setNeedsPhoto] = useState(false);
  const [onlyLow, setOnlyLow] = useState(false);
  const [onlyShared, setOnlyShared] = useState(false);
  const [threshold, setThreshold] = useState(30);

  useEffect(() => {
    if (!user) return;
    api.get("/admin/check").then(() => {
      setAllowed(true);
      api.get("/admin/products").then(({ data }) => setProducts(data)).catch(() => {});
      api.get("/admin/orders").then(({ data }) => setOrders(data)).catch(() => {});
      api.get("/admin/settings").then(({ data }) => setThreshold(data.margin_alert)).catch(() => {});
    }).catch(() => setAllowed(false));
  }, [user]);

  if (loading) return <main className="pt-32 min-h-[60vh]" />;
  if (!user || allowed === false)
    return (
      <main data-testid={ADMIN.denied} className="pt-36 pb-10 max-w-xl mx-auto px-6 text-center min-h-[60vh]">
        <h1 className="font-heading text-3xl font-extrabold">Shop admin</h1>
        <p className="mt-4 text-stone-600">{user ? "This account doesn't have admin access." : "Sign in with the shop owner's Google account."}</p>
        {!user && <button data-testid={ACCOUNT.loginButton} onClick={login} className="mt-8 px-7 py-3.5 rounded-full bg-[#1C1917] text-white hover:bg-[#E06D44] transition-colors">Sign in with Google</button>}
      </main>
    );
  if (!allowed) return <main className="pt-32 min-h-[60vh]" />;

  const replace = (list, key, item) => list.map((x) => (x[key] === item[key] ? item : x));
  const shared = sharedSlugs(products);
  const shown = products.filter((p) => p.name.toLowerCase().includes(q.toLowerCase()) && (!needsPhoto || isAiPhoto(p.image)) && (!onlyLow || lowMargin(p, threshold)) && (!onlyShared || shared.has(p.slug)));
  const toShip = orders.filter((o) => o.status !== "shipped").length;

  return (
    <main data-testid={ADMIN.page} className="pt-28 pb-10 max-w-7xl mx-auto px-6 lg:px-10">
      <h1 className="font-heading text-4xl font-extrabold tracking-tight">Shop admin</h1>
      <AdminDashboard refreshKey={`${orders.length}-${toShip}`} />
      <LowMarginPanel products={products} threshold={threshold} onThreshold={setThreshold} />
      <div className="mt-10 flex flex-wrap gap-2">
        {[["orders", `Orders (${toShip} to ship)`], ["products", `Products (${products.length})`], ["new", "Add product"], ["clips", "Kiddee clips"], ["drafts", "Claude drafts"], ["chats", "Shopper chats"], ["reviews", "Reviews"], ["messages", "Messages"], ["bundles", "Bundles"], ["weekly", "Weekly email"], ["google", "Google setup"], ["stripe", "Stripe setup"], ["live", "Live check"], ["blog", "Blog"]].map(([id, label]) => (
          <button key={id} data-testid={ADMIN.tab(id)} onClick={() => setTab(id)} className={`px-4 py-2 rounded-full text-sm border transition-colors ${tab === id ? "bg-[#1C1917] text-white border-[#1C1917]" : "border-[#E7E0D3] hover:bg-[#F0EBE1]"}`}>{label}</button>
        ))}
      </div>
      {tab === "clips" && <AdminClips />}
      {tab === "drafts" && <AdminDrafts products={products} onProductUpdated={(slug, patch) => setProducts((l) => l.map((p) => (p.slug === slug ? { ...p, ...patch } : p)))} />}
      {tab === "chats" && <AdminChats />}
      {tab === "reviews" && <AdminReviews />}
      {tab === "messages" && <AdminMessages />}
      {tab === "bundles" && <AdminBundles products={products} />}
      {tab === "weekly" && <AdminWeeklyReport />}
      {tab === "google" && <AdminMerchantSetup />}
      {tab === "stripe" && <AdminStripeSetup />}
      {tab === "live" && <AdminLiveCheck />}
      {tab === "blog" && <AdminBlog products={products} />}
      {tab === "orders" && (
        <ul className="mt-8 divide-y divide-[#F0EBE1]">
          {orders.length === 0 && <p className="text-stone-500">No paid orders yet.</p>}
          {orders.map((o) => <OrderRow key={o.order_id} order={o} onSaved={(d) => setOrders((l) => replace(l, "order_id", d))} />)}
        </ul>
      )}
      {tab === "products" && (
        <>
          <AdminSharedPhotos products={products} onPick={(name) => { setQ(name); setOnlyShared(false); setNeedsPhoto(false); setOnlyLow(false); }} />
          <div className="mt-8 flex flex-wrap items-center gap-4">
            <input data-testid={ADMIN.search} value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search products" className="w-full max-w-sm px-4 py-2.5 rounded-full border border-[#E7E0D3]" />
            <label className="flex items-center gap-2 text-sm"><input data-testid={ADMIN.photoFilter} type="checkbox" checked={needsPhoto} onChange={(e) => setNeedsPhoto(e.target.checked)} /> Only products needing a real photo ({products.filter((p) => isAiPhoto(p.image)).length})</label>
            <label className="flex items-center gap-2 text-sm"><input data-testid={ADMIN.lowMarginFilter} type="checkbox" checked={onlyLow} onChange={(e) => setOnlyLow(e.target.checked)} /> Only low margin ({products.filter((p) => p.active && lowMargin(p, threshold)).length})</label>
            <label className="flex items-center gap-2 text-sm"><input data-testid="admin-shared-photo-filter" type="checkbox" checked={onlyShared} onChange={(e) => setOnlyShared(e.target.checked)} /> Only shared stand-in photos ({shared.size})</label>
          </div>
          <ul className="mt-4 divide-y divide-[#F0EBE1]">
            {shown.map((p) => <AdminProductRow key={p.slug} product={p} threshold={threshold} onSaved={(d) => setProducts((l) => replace(l, "slug", d))} />)}
          </ul>
        </>
      )}
      {tab === "new" && (
        <div className="mt-8">
          <AdminNewProduct onCreated={(d) => { setProducts((l) => [d, ...l]); setQ(""); setTab("products"); }} />
        </div>
      )}
    </main>
  );
}
